import _ from 'lodash';
import { ITEMS } from '../../../../../constants';
import geometry from '../../../../../scene/util/geometry';
import productService from '../../../../../services/products';
import tacHelpers, { digAllItems } from '../../../tacHelpers';
import validationErrors from '../validationErrors';

/**
 * Gets no of points a specific insert should add to the section total
 *
 * @param {Object} item The insert to look up
 * @returns {Number} The no of points the insert should add
 */
export const getInsertPoints = item => {
  switch (item.filter.type) {
    case ITEMS.DRAWER:
      return 1;
    case ITEMS.SHELF:
      return 1;
    case ITEMS.SHELF_CLOTHES_RAIL:
      return 1;
    default:
      return 0;
  }
};

/**
 * Gets the dimensions and coordinates of the three "zones of interest"
 * (top, bottom, middle) needed for the stability validation
 *
 * @param {Object} section The section to get zones for
 * @returns {Array} A list of the two zones in the requested section
 */
const getStabilityZones = section => {
  const { width, height, depth } = section;
  const localSection = { x: 0, y: 0, z: 0, width, height, depth };

  const bottom = {
    ...localSection,
    y: 0,
    height: 0.3 * section.height,
  };

  const top = {
    ...localSection,
    y: section.height - 0.3 * section.height,
    height: 0.3 * section.height,
  };

  return [bottom, top];
};

export const getAllSubItemInserts = item => {
  if (!item.items.length) return item;
  // Reusing recursive search algorithm
  return digAllItems(item.items).filter(productService.isInsert);
};

/**
 * Repositions an item so that its coordinates are relative to the section that it is
 * a descendand of, instead of just relative to the parent of the item. This is useful
 * for collision detection when the item is not a direct child of the section, but a
 * grandchild or further descendant.
 *
 * @param {Object} item The item of which we want to get the position relative to the section.
 * @param {Object} section The section of which the item is a descendant.
 * @returns {Object} An object corresponding to the item but with coordinates relative to the section.
 */
const getRepositionedItem = (item, section) => {
  const parent = tacHelpers.getParent(section, item);
  const hasReachedSection = parent.itemid === section.itemid;

  const itemRepositioned = _.clone(item);
  if (!hasReachedSection) {
    const parentRepositioned = getRepositionedItem(parent, section);
    itemRepositioned.x += parentRepositioned.x;
    itemRepositioned.y += parentRepositioned.y;
    itemRepositioned.z += parentRepositioned.z;
  }

  return itemRepositioned;
};

/**
 * Gets the (eventual) stability errors for a section
 *
 * @param {Object} section The section to validate
 * @param {Object} inserts A list fo all the inserts in the section
 * @returns {Array} A list of all stability errors found on the section
 */
export const getStabilityErrors = (section, inserts) => {
  const stabilityErrors = [];
  const isSectionSideUnits = productService.isType(
    section,
    ITEMS.SECTION_SIDE_UNITS
  );

  if (isSectionSideUnits) {
    const insertsWithPoints = inserts.map(insert => ({
      ...insert,
      stabilityPoints: getInsertPoints(insert, 'stability', section),
    }));

    const insertsWithPointsRepositioned = insertsWithPoints.map(
      insertWithPoints => getRepositionedItem(insertWithPoints, section)
    );

    const isValidZone = area =>
      insertsWithPointsRepositioned.some(
        insert => insert.stabilityPoints && geometry.collides(insert, area)
      );

    const enoughInsertPointsInRelevantAreas =
      getStabilityZones(section).every(isValidZone);

    if (!enoughInsertPointsInRelevantAreas) {
      const unstableZones = getStabilityZones(section)
        .map(zone => {
          return {
            min: zone.y,
            max: zone.height + zone.y,
            valid: isValidZone(zone),
          };
        })
        .filter(zone => !zone.valid);
      stabilityErrors.push({
        ...validationErrors.STABILITY_ZONES_UNCOVERED_ELVARLI,
        itemid: section.itemid,
        zones: unstableZones,
      });
    }
  }

  return stabilityErrors;
};

/**
 * Takes two sections, one to the left and one to the right, and decides
 * whether they are merged or not.
 * If either leftSection or rightSection is null/undefined, the sections
 * are considered not to be merged.
 *
 * @param {Object} leftSection The section to the left (or null/undefined).
 * @param {Object} rightSection The section to the right (or null/undefined).
 * @returns {Boolean} Whether the two sections are merged or not.
 */
const areSectionsMerged = (leftSection, rightSection) => {
  if (!leftSection || !rightSection) return false;

  const sectionsOverlap = rightSection.x < leftSection.x + leftSection.width;
  return sectionsOverlap;
};

/**
 * Takes two sections, one to the left and one to the right, and returns the
 * "middle" item of their shared side unit. The left / right sections are also
 * allowed to be null/undefined.
 * Important rules:
 * 1) If both left and right sections are defined, but they are not merged, they
 *    don't have a shared side unit, so null is returned.
 * 2) If the left section is defined, but the right section is null/undefined, the
 *    shared side unit is considered to be the right side unit of the left section.
 * 3) If the right section is defined, but the left section is null/undefined, the
 *    shared side unit is considered to be left side unit of the right section.
 *
 * @param {Object} leftSection The section to the left (or null/undefined).
 * @param {Object} rightSection The section to the right (or null/undefined).
 * @returns {Object} The "middle" item of the shared side unit (or null).
 */
const getSharedSideUnitMiddleItemOfSections = (leftSection, rightSection) => {
  if (
    leftSection &&
    rightSection &&
    !areSectionsMerged(leftSection, rightSection)
  )
    return null;

  const sideUnitMiddleItemFilter = item =>
    productService.isType(item, ITEMS.SIDE_PANEL) &&
    item.id.includes('_middle');
  const getLeftmostOf = items =>
    items ? items.sort((item1, item2) => item1.x - item2.x)[0] : null;
  const getRightmostOf = items =>
    items ? items.sort((item1, item2) => item2.x - item1.x)[0] : null;
  const getLeftSideUnitMiddleItemOfSection = section =>
    getLeftmostOf(section?.items?.filter(sideUnitMiddleItemFilter));
  const getRightSideUnitMiddleItemOfSection = section =>
    getRightmostOf(section?.items?.filter(sideUnitMiddleItemFilter));

  let sharedSideUnitMiddleItem = null;
  if (rightSection) {
    // Doesn't matter in this case whether left section is null/undefined or not.
    sharedSideUnitMiddleItem = getLeftSideUnitMiddleItemOfSection(rightSection);
  } else if (leftSection) {
    // We have a left section but the right section is null/undefined.
    sharedSideUnitMiddleItem = getRightSideUnitMiddleItemOfSection(leftSection);
  } // If we have neither left nor right section, we'll keep the shared side unit middle item as null.

  return sharedSideUnitMiddleItem;
};

/**
 * Checks whether there are enough brackets with holes to accommodate the "shelf + clothes rail" items
 * in the configuration. The validation intentionally assumes that the user won't "borrow" brackets with
 * holes from other side units and only assembles each side unit using the parts delivered in that package.
 * This check is only relevant when building with side unit sections, and will never trigger a validation
 * error for a post sections configuration.
 *
 * @param {Object} tac The TAC to analyze.
 * @returns {Array} The validation errors.
 */
const getNotEnoughBracketsWithClothesRailHolesErrors = tac => {
  const NUMBER_OF_BRACKETS_WITH_HOLES_PROVIDED_PER_SIDE_UNIT = 2;

  const sectionsSideUnits = tac.items
    .filter(item => productService.isType(item, ITEMS.SECTION_SIDE_UNITS))
    .sort((s1, s2) => s1.x - s2.x);

  // We want "null sections" where there are "holes" in the configuration, i.e., where the sections
  // aren't merged, and also to the left of the first section, and to the right of the last section.
  const sectionsSideUnitsWithNullSections = [];
  for (let i = 0; i < sectionsSideUnits.length; i++) {
    const thisSection = sectionsSideUnits[i];
    const sectionToTheLeft = i > 0 ? sectionsSideUnits[i - 1] : null;
    if (!areSectionsMerged(sectionToTheLeft, thisSection))
      sectionsSideUnitsWithNullSections.push(null);
    sectionsSideUnitsWithNullSections.push(thisSection);
  }
  sectionsSideUnitsWithNullSections.push(null); // "null section" to the right of the last real section.

  const pairsOfSections = [];
  for (let i = 0; i < sectionsSideUnitsWithNullSections.length - 1; i++) {
    const leftSection = sectionsSideUnitsWithNullSections[i];
    const rightSection = sectionsSideUnitsWithNullSections[i + 1];
    pairsOfSections.push({
      leftSection,
      rightSection,
    });
  }

  const shelvesClothesRailPerSection = new Map();
  sectionsSideUnits.forEach(section => {
    shelvesClothesRailPerSection.set(
      section,
      section.items
        .filter(item => productService.isType(item, ITEMS.SHELF_CLOTHES_RAIL))
        .sort((scr1, scr2) => scr2.y - scr1.y)
    );
  });
  shelvesClothesRailPerSection.set(null, []); // "null sections" don't have any shelves clothes rail.

  const sideUnitsWithoutEnoughBracketsWithHoles = [];
  pairsOfSections.forEach(({ leftSection, rightSection }) => {
    const scrsInLeft = shelvesClothesRailPerSection.get(leftSection);
    const scrsInRight = shelvesClothesRailPerSection.get(rightSection);
    const numberOfBracketsWithHolesNeededOnSharedSideUnit =
      scrsInLeft.length + // One for each sh. cl. rail in left section...
      scrsInRight.filter(scrInRight => {
        return !scrsInLeft.some(scrInLeft => scrInLeft.y === scrInRight.y);
      })
        .length; /* ...but only count those sh. cl. rail in the right section for which there
                    isn't a sh. cl. rail in the left section at the same height. */

    if (
      numberOfBracketsWithHolesNeededOnSharedSideUnit >
      NUMBER_OF_BRACKETS_WITH_HOLES_PROVIDED_PER_SIDE_UNIT
    )
      sideUnitsWithoutEnoughBracketsWithHoles.push(
        getSharedSideUnitMiddleItemOfSections(leftSection, rightSection)
      );
  });

  const notEnoughBracketsWithHolesErrors = [];
  sideUnitsWithoutEnoughBracketsWithHoles.forEach(sideUnit => {
    notEnoughBracketsWithHolesErrors.push({
      ...validationErrors.NOT_ENOUGH_BRACKETS_WITH_HOLES_FOR_CLOTHES_RAIL_ELVARLI,
      itemid: sideUnit,
    });
  });

  return notEnoughBracketsWithHolesErrors;
};

/**
 * Validates an ELVARLI TAC according to the stability rules
 *
 * @param {Object} tac The complete TAC to validate
 * @returns {Array} A list of all stability errors found in the TAC
 */
const validate = tac => {
  const allErrors = tac.items.reduce((errors, item) => {
    if (productService.isSection(item)) {
      const inserts = item.items.filter(productService.isInsert);
      const insertsIncludingSubInserts = inserts
        .reduce((acc, insert) => [...acc, getAllSubItemInserts(insert)], [])
        .flat();
      errors.push(...getStabilityErrors(item, insertsIncludingSubInserts));
    }
    return errors;
  }, []);
  allErrors.push(...getNotEnoughBracketsWithClothesRailHolesErrors(tac));

  return allErrors;
};

export default {
  validate,
};
